// Angular
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
// PrimeNG
import { StyleClassModule } from 'primeng/styleclass';
import { RippleModule } from 'primeng/ripple';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AccordionModule } from 'primeng/accordion';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
// Components
import { ShippingStepComponent } from './steps/shipping-step/shipping-step.component';
import { PaymentStepComponent } from './steps/payment-step/payment-step.component';
import { ContactStepComponent } from './steps/contact-step/contact-step.component';
import { SummaryOrderComponent } from './components/summary-order/summary-order.component';
// Constants
import {
  BACK_BUTTON_LABELS,
  getBackButtonLabel,
  SUBMIT_BUTTON_LABEL,
} from './button-labels';
// Services
import { ScrollService } from '@core/utils-v2/scroll/scroll.service';
import { SessionService } from '@core/services-v2/session/session.service';

import {
  IShoppingCart,
  IShoppingCartGroup,
} from '@core/models-v2/cart/shopping-cart.interface';
import { CartService } from '@core/services-v2/cart.service';
import { environment } from '@env/environment';
import { ShippingDateItem } from '@shared/interfaces/address';
import { PaymentType } from './steps/payment-step/models/payment-type.type';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaymentMethodService } from '@core/services-v2/payment-method.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { PaymentDocumentType } from '@core/enums/payment-document-type.enum';
import { GtmService } from '@core/utils-v2/gtm/gtm.service';
import { IKhipuBank } from '@core/models-v2/payment-method/khipu-bank.interface';
import { ISession } from '@core/models-v2/auth/session.interface';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Modal } from './components/modal/modal.component';
import { PaymentMethodType } from '@core/enums/payment-method.enum';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { GuestStorageService } from '@core/storage/guest-storage.service';
import { QuotationApiService } from '@core/services-v2/quotation/quotation-api/quotation-api.service';
import { CartStorageService } from '@core/services-v2/cart/cart-storage/cart-storage.service';
import { PurchaseOrderForm } from './steps/payment-step/components/purchase-order-form/models/purchase-order-form.interface';
import { PaymentMethodPurchaseOrderRequestService } from '@core/services-v2/payment-method-purchase-order-request.service';
import { DocumentType } from '@core/enums/document-type.enum';
import { UserRoleType } from '@core/enums/user-role-type.enum';
import { SmsModalComponent } from './components/sms-modal/sms-modal.component';
import { IPaymentPurchaseOrder } from '@core/models-v2/payment-method/payment-purchase-order.interface';
import { ReloadModalComponent } from './components/reload-modal/reload-modal.component';
import { BlockUiService } from '@core/services-v2/block-ui/block-ui.service';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { TermsOfServiceInputComponent } from '@shared/components-v3/inputs/terms-of-service-input/terms-of-service-input.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { KhipuSidebarComponent } from './components/khipu-sidebar/khipu-sidebar.component';
import { InvoiceForms } from './steps/payment-step/components/invoice-form/models/invoice-forms.interface';
import { CreateGuestRequestDto } from '@core/services-v2/customer/dtos/guest/create-guest-request.dto';
import { CustomerApiService } from '@core/services-v2/customer/customer-api.service';
import { CheckoutStorageService } from './services/checkout-storage/checkout-storage.service';
import { UpdateCartCheckoutRequestDto } from '@core/services-v2/cart/cart-api/dtos/update-cart-checkout-request.dto';
import { InvoiceForm } from './steps/payment-step/components/invoice-form/models/invoice-form.interface';

const NG_MODULES = [CommonModule, ReactiveFormsModule];
const PRIME_MODULES = [
  DropdownModule,
  InputNumberModule,
  ButtonModule,
  InputTextModule,
  DividerModule,
  RadioButtonModule,
  AccordionModule,
  RippleModule,
  CheckboxModule,
  StyleClassModule,
  ProgressSpinnerModule,
  DynamicDialogModule,
  ConfirmDialogModule,
];
const COMPONENTS = [
  ShippingStepComponent,
  PaymentStepComponent,
  ContactStepComponent,
  SummaryOrderComponent,
  TermsOfServiceInputComponent,
  KhipuSidebarComponent,
];

const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

declare let dataLayer: any;
declare var VisanetCheckout: any;

export interface Archivo {
  archivo: File;
  nombre: string;
  icon: string;
  extension: string;
}

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [NG_MODULES, COMPONENTS, PRIME_MODULES, DIRECTIVES],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
  providers: [DialogService, MessageService, ConfirmationService],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  //#region ViewChild components
  @ViewChild('paymentStepComponent')
  paymentStepComponent!: PaymentStepComponent;
  @ViewChild('contactStepComponent')
  contactStepComponent!: ContactStepComponent;
  @ViewChild('shippingStepComponent')
  shippingStepComponent!: ShippingStepComponent;
  @ViewChild('khipuSidebarComponent') bancosSidebar!: KhipuSidebarComponent;

  //#endregion

  //#region ViewChild elements
  @ViewChild('termsOfServiceFormElement')
  termsOfServiceFormElement!: ElementRef<HTMLFormElement>;
  //#endregion

  @ViewChild('bankmodal', { static: false }) content: any;

  private readonly platformId: Object = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly scrollService = inject(ScrollService);
  private readonly sessionService = inject(SessionService);
  public readonly cartService = inject(CartService);
  private readonly cartApiService = inject(CartApiService);
  private readonly quotationApiService = inject(QuotationApiService);
  private readonly cartStorageService = inject(CartStorageService);
  private readonly paymentMethodService = inject(PaymentMethodService);
  private readonly _gtmService = inject(GtmService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(DialogService);
  private readonly guestStorage = inject(GuestStorageService);
  private readonly customerApiService = inject(CustomerApiService);
  private readonly paymentMethodPurchaseOrderRequestService = inject(
    PaymentMethodPurchaseOrderRequestService
  );
  private readonly blockUiService = inject(BlockUiService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  private readonly gmtService = inject(GtmService);

  isLoggedIn = signal<boolean>(this.sessionService.isLoggedIn());
  step = signal<number>(this.isLoggedIn() ? 2 : 1);
  steps = signal<number>(3);
  backButtonLabel = computed(() =>
    getBackButtonLabel(
      this.step() as keyof typeof BACK_BUTTON_LABELS,
      this.isLoggedIn()
    )
  );
  submitButtonLabel = computed(
    () => SUBMIT_BUTTON_LABEL[this.step() as keyof typeof SUBMIT_BUTTON_LABEL]
  );

  cartSession!: IShoppingCart;
  countryCode = environment.country;
  isQuotationVisible = environment.visibility.quotation;
  isTermsOfServiceVisible =
    environment.legalResourcesVisibility.termsOfServiceOnPayment;
  isLoadingPickupDates: boolean = false;
  shippingDates = [];
  pickupDescription: string = '';
  shippingDays: ShippingDateItem[] = [];
  isLoadingQuotation = false;
  methodPayment!: PaymentType | null;
  userSession!: ISession;
  modalRef!: BsModalRef;
  ref: DynamicDialogRef | undefined;
  totalCarro: number = 0;
  purchaseOrderId: string = ''; // se utiliza para pasar al modal de ingreso codigo sms
  termsOfServiceForm!: FormGroup;
  isSidebarVisible: boolean = false;

  private isUpToMediumScreen$: Observable<boolean> = this.breakpointObserver
    .observe(['(max-width: 991px)'])
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      map((result) => result.matches)
    );
  isUpToMediumScreen: Signal<boolean> = toSignal(this.isUpToMediumScreen$, {
    initialValue: false,
  });

  get disclaimerField() {
    return this.termsOfServiceForm.get('disclaimer');
  }

  constructor() {
    if (this.isTermsOfServiceVisible) {
      this.buildForm();
    }
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        this.step();
        this.scrollService.scrollToTop();
      });
    }
  }

  ngOnInit(): void {
    this.cartSession = this.cartStorageService.get()!;
    this.userSession = this.sessionService.getSession();
    this.isTermsOfServiceVisible = this.termsOfServiceVisible();
    this.updateStep();

    // hilo para escuchar el close del modal
    this.paymentMethodService.closemodal$.subscribe((r) => {
      if (r) {
        this.modalRef.hide();
      }
    });
    this.paymentMethodService.banco$.subscribe((r) => {
      this.createTransactionKhipu(r);
    });

    this.route.queryParams.subscribe((params: any) => {
      if (this.countryCode == 'cl') {
        if (params['status']) this.showRejectMessage(params);
      } else {
        const decodedParams = JSON.parse(atob(params.params));
        if (decodedParams.status) this.showRejectMessage(params);
      }
    });
    // Obtiene el total del carro
    this.cartService.total$.subscribe((r) => {
      this.totalCarro = r || 0;
    });
    if (isPlatformBrowser(this.platformId)) {
      this.gmtService.beginCheckout(dataLayer, this.cartSession);
    }
  }

  private termsOfServiceVisible(): boolean {
    if (this.isTermsOfServiceVisible && this.countryCode === 'pe') return true;
    if (
      this.isTermsOfServiceVisible &&
      this.countryCode === 'cl' &&
      !this.isLoggedIn()
    )
      return true;
    return false;
  }

  private showRejectMessage(params: any) {
    if (this.countryCode == 'cl') {
      if (params.status === 'rejected') {
        this.step.set(3);
        if (params['paymentMethod'] === PaymentMethodType.MERCADOPAGO) {
          const reason = params.message;
          let alertCart = {
            detalleMensaje: `La transacción en MercadoPago se ha anulado por un ${reason.toLowerCase()}.`,
            mostrarBotonVolverIntentar: true,
            proceso: 'Anulado',
          };
          this.showModal(alertCart);
        } else {
          const message = params.message;
          let alertCart = {
            pagoValidado: false,
            detalleMensaje: message,
            mostrarBotonVolverIntentar: true,
          };
          this.showModal(alertCart);
        }
      }
    } else {
      let params_query = params.params;
      const decodedParams = JSON.parse(atob(params_query));

      if (decodedParams.status === 'rejected') {
        this.step.set(3);
        const pedidoCart = decodedParams.pedido;
        const fehaPedidoCart = decodedParams.feha_pedido;
        const descripcionPedido = decodedParams.action_descripcion;
        let alertCart = {
          pagoValidado: false,
          pedido: 'Pedido: ' + pedidoCart,
          fecha: fehaPedidoCart,
          detalleMensaje: 'Motivo: ' + descripcionPedido,
          mostrarBotonVolverIntentar: true,
        };
        this.showModal(alertCart);
      }
    }
  }

  showModal(info: any) {
    this.ref = this.dialogService.open(Modal, {
      header: '',
      width: '50vw',
      modal: true,
      data: info,
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
    });
  }

  async submit(): Promise<void> {
    if ([1, 2].includes(this.step())) {
      this.setNextStep();
    } else if (this.step() === 3) {
      const paymentDetail = this.paymentStepComponent.submit();
      if (this.isTermsOfServiceVisible && this.isUpToMediumScreen()) {
        this.termsOfServiceFormElement.nativeElement.dispatchEvent(
          new Event('submit', { cancelable: true })
        );
        if (!this.termsOfServiceForm.valid) return;
      }
      if (!paymentDetail) return;
      await this.onPaymentFormSubmit(paymentDetail);
    }
  }

  /**
   * Maneja el evento submit desde el componente `PaymentStepComponent`.
   * @param {PaymentType} paymentType - Tipo de pago seleccionado.
   */
  async onPaymentFormSubmit({
    paymentType,
    purchaseOrder,
    invoice,
  }: {
    paymentType: PaymentType;
    purchaseOrder: PurchaseOrderForm | null;
    invoice: InvoiceForms | null;
  }): Promise<void> {
    this.blockUiService.set(true);
    if (this.isValidateCutOffTime()) {
      await this.updateCartBeforeCheckout(invoice);

      this.methodPayment = paymentType;
      if (this.methodPayment === 'webPay') this.createTransactionTransbank();
      else if (this.methodPayment === 'mercadopago')
        this.createTransactionMercadoPago();
      else if (this.methodPayment === 'khipu') this.createTransactionKhipu();
      else if (this.methodPayment === 'Niubiz') this.createTransactionNiubiz();
      else if (this.methodPayment === 'ordenCompra') {
        if (this.userSession.userRole === UserRoleType.SUPERVISOR) {
          if (!this.userSession.creditLine?.requiresConfirmation)
            this.paymentOV(purchaseOrder);
          else this.paymentOvSms(purchaseOrder);
        } else if (this.userSession.userRole === UserRoleType.BUYER) {
          this.purchaseRequestAll(purchaseOrder);
        }
      }
    } else this.reloadShipping();
  }

  onSubmitTermsOfServiceForm(): void {
    if (this.termsOfServiceForm.valid) return;
    this.termsOfServiceForm.markAllAsTouched();
  }

  /**
   * Incrementa el valor del paso actual en uno.
   *
   * Este método actualiza el valor del paso actual llamando a la función `update`
   * y aumentando el valor en uno.
   *
   * @private
   */
  private setNextStep(): void {
    let isValid = false;
    if (this.step() === 1) {
      isValid = this.contactStepComponent.submit();
    } else if (this.step() === 2) {
      isValid = this.shippingStepComponent.submit();
    }

    if (!isValid) return;
    this.step.update((value) => value + 1);
    this.updateStep();
  }

  goBack(): void {
    if (this.step() === 1 || (this.step() === 2 && this.isLoggedIn())) {
      this.router.navigate(['/carro-compra/resumen']);
    } else if (this.step() < 1) return;
    this.step.update((value) => value - 1);
    this.updateStep();
  }

  private buildForm(): void {
    this.termsOfServiceForm = this.fb.group({
      disclaimer: [null],
    });
  }
  /**
   * Muestra un dialogo de confirmación para generar una cotización.
   * @param event - Evento del DOM que contiene la referencia al elemento button.
   */
  comfirmGenerateQuotation(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        '¿Seguro que deseas realizar un presupuesto? Al hacerlo, tu carrito se vaciará. Para recuperar los productos, deberás recargarlos desde el módulo "Presupuestos".',
      header: 'Generar presupuesto',
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancelar',
      acceptLabel: 'Generar',
      acceptButtonStyleClass: 'p-button-text p-button-text',
      rejectButtonStyleClass: 'p-button-danger p-button-text',
      accept: () => this.generateQuotation(),
    });
  }

  /**
   * Generar cotización.
   */
  generateQuotation(): void {
    this.isLoadingQuotation = true;
    this.blockUiService.set(true);
    this.quotationApiService.create(this.cartSession._id!).subscribe({
      next: (response) => {
        this.isLoadingQuotation = false;
        this.blockUiService.set(false);
        this.cartService.load();
        this.router.navigate([
          '/carro-compra/comprobante-de-cotizacion',
          response.shoppingCart.salesId,
        ]);
      },
      error: (err) => {
        console.error(err);
        this.isLoadingQuotation = false;
        this.blockUiService.set(false);
      },
    });
  }
  /**
   * Actualiza el paso actual del carrito.
   *
   * @returns
   */
  private updateStep(): void {
    if (!this.cartSession._id) return;
    this.cartApiService
      .updateStep(this.cartSession._id, this.step())
      .subscribe();
  }

  /**
   * Verifica el horario de corte.
   * @returns {boolean} - Retorna `true` si el horario de corte es válido, `false` en caso contrario.
   */
  private isValidateCutOffTime(): boolean {
    this.cartSession = this.cartStorageService.get()!;
    let isValidate = true;
    if (this.cartSession.groups?.length) {
      let updateDateCart = new Date(this.cartSession.updatedAt ?? '');
      let nowDate = new Date();
      this.cartSession.groups?.forEach((group: IShoppingCartGroup) => {
        let cutOffTime = new Date(group.shipment?.cutOffDateTime ?? '');
        if (updateDateCart < cutOffTime && nowDate >= cutOffTime) {
          isValidate = false;
        }
      });
      return isValidate;
    } else {
      let updateDateCart = new Date(this.cartSession.updatedAt ?? '');
      let nowDate = new Date();
      let cutOffTime = new Date(
        this.cartSession.shipment?.cutOffDateTime ?? ''
      );
      if (updateDateCart < cutOffTime && nowDate >= cutOffTime) {
        return false;
      } else {
        return isValidate;
      }
    }
  }

  /**
   * Agregar informacion de pago al GTM
   */
  addPaymentInfoToGTM(method: string) {
    this._gtmService.addPaymentInfo({
      dataLayer,
      shoppingCart: this.cartSession,
      paymentType: method,
    });
  }
  /**
   *  Crear paga con transbank ( webpay)
   */
  private async createTransactionTransbank() {
    try {
      this.addPaymentInfoToGTM('WEBPAY');
      //await this.prepararCarroPrePago(); // solamente si es invitado funciona
      this.paymentMethodService.redirectToWebpayTransaction({
        shoppingCartId: this.cartSession._id!.toString(),
      });
    } catch (e) {
      this.blockUiService.set(false);
      console.log('error', e);
      console.log('Ha ocurrido un error al gestionar el pago.');
    }
  }

  /**
   *  Crear pago con mercadopago
   * @returns
   */
  private async createTransactionMercadoPago() {
    try {
      this.addPaymentInfoToGTM('MERCADOPAGO');
      //await this.prepararCarroPrePago(); // solamente si es invitado funciona
      this.paymentMethodService.redirectToMercadoPagoTransaction({
        shoppingCartId: this.cartSession._id!.toString(),
      });
    } catch (e) {
      this.blockUiService.set(false);
      console.log('error', e);
      console.log('Ha ocurrido un error al gestionar el pago.');
    }
  }

  /**
   *  Crear pago con khipu
   * @returns
   */
  private async createTransactionKhipu(banco?: IKhipuBank) {
    if (!banco) {
      this.blockUiService.set(false);
      this.bancosSidebar.show();
    } else {
      try {
        this.blockUiService.set(true);
        this.addPaymentInfoToGTM('KHIPU');
        //await this.prepararCarroPrePago(); // solamente si es invitado funciona
        this.paymentMethodService.redirectToKhipuTransaction({
          shoppingCartId: this.cartSession._id!.toString(),
          bankId: banco ? banco.bankId : '',
          bankName: banco ? banco.name : '',
          payerName: this.userSession.email,
          payerEmail: this.userSession.email,
        });
      } catch (e) {
        this.blockUiService.set(false);
        console.log('error', e);
        console.log('Ha ocurrido un error al gestionar el pago.');
      }
    }
  }

  /**
   *  Crear pago con niubiz
   * @returns
   */
  private async createTransactionNiubiz() {
    try {
      this.addPaymentInfoToGTM('NIUBIZ');
      //await this.prepararCarroPrePago(); // solamente si es invitado funciona
      this.paymentMethodService.getIp().subscribe((data: any) => {
        let ipaddress = data.ip;
        this.paymentMethodService
          .redirectToNiubizTransaction({
            shoppingCartId: this.cartSession._id!.toString(),
            ipaddress: ipaddress,
            typeVoucher: 'receipt', // arreglar *
          })
          .subscribe((data) => {
            let token = data.sessionKey;
            let purchaseNumber = data.expirationTime;
            const totalAmount = this.totalCarro.toFixed(2);
            VisanetCheckout.configure({
              sessiontoken: token,
              channel: 'web',
              merchantid: environment.codmerchant,
              purchasenumber: purchaseNumber,
              amount: totalAmount,
              expirationminutes: '20',
              timeouturl: 'about:blank',
              merchantlogo: 'https://api.epysa.com.pe:447/Logos-Pago.png',
              formbuttoncolor: '#000000',
              action:
                environment.linkPago +
                purchaseNumber +
                '&amount=' +
                totalAmount +
                '&email=' +
                this.cartSession.notification?.email,
              cancel: this.onCancelNiubiz.bind(this),
            });
            VisanetCheckout.open();
          });
      });
    } catch (e) {
      this.blockUiService.set(false);
      console.log('error', e);
      console.log('Ha ocurrido un error al gestionar el pago.');
    }
  }

  onCancelNiubiz(): void {
    this.blockUiService.set(false);
  }

  /**
   * Actualizar el carro de compras antes de realizar el pago.
   * - Actualiza el tipo de documento de pago (boleta, factura).
   * - Antes era prepay.
   * @returns
   */
  updateCartBeforeCheckout(invoiceParams: InvoiceForms | null): Promise<any> {
    const DEFAULT_PAYMENT_TYPE = this.sessionService.isEnterprise()
      ? PaymentDocumentType.INVOICE
      : PaymentDocumentType.RECEIPT;

    const paymentDocumentType =
      this.checkoutStorageService.getPaymentDocumentType() ||
      DEFAULT_PAYMENT_TYPE;

    const isReceipt = paymentDocumentType === PaymentDocumentType.RECEIPT;
    const isLoggedIn = this.sessionService.isLoggedIn();
    const isEnterprise = this.sessionService.isEnterprise();
    const updateCartRequest: UpdateCartCheckoutRequestDto = {
      cartId: this.cartSession._id!,
      invoiceType: paymentDocumentType,
      documentId: '',
    };
    //const address = this.checkoutStorageService.getDeliveryAddress();
    /*const isValidAddress: boolean =
      paymentDocumentType.toLowerCase() === PaymentDocumentType.INVOICE &&
      address != null;*/

    // Obtener documentId =========================================================
    const guest = this.guestStorage.get();
    //
    if (!isLoggedIn) {
      // Si no esta logueado
      if (!guest) {
        throw new Error('No se han ingresado los datos del invitado');
      }
      // Si es boleta
      if (isReceipt) {
        updateCartRequest.documentId = guest.documentId;
      } else {
        // Si es factura
        if (!invoiceParams) {
          throw new Error('No se han ingresado los datos de facturación');
        }
        const { business, invoiceAddress, isRegistered } = invoiceParams;

        if (!business) {
          throw new Error(
            'No se han ingresado los datos de la empresa para el proceso de facturación'
          );
        }
        if (!isRegistered) {
          updateCartRequest.business = { ...business, phone: guest.phone };
        }
        updateCartRequest.documentId = business.documentId;

        if (invoiceAddress) {
          updateCartRequest.invoiceAddress = invoiceAddress;
        }
      }
    } else if (isReceipt || isEnterprise) {
      // Si es Boleta o Empresa:
      updateCartRequest.documentId = this.userSession.documentId;
    } else {
      // Si es Factura y B2C:
      if (!invoiceParams) {
        throw new Error('No se han ingresado los datos de facturación');
      }
      const { business, invoiceAddress, invoiceAddressId, isRegistered } =
        invoiceParams;

      if (!business) {
        throw new Error(
          'No se han ingresado los datos de la empresa para el proceso de facturación'
        );
      }
      if (!isRegistered) {
        updateCartRequest.business = {
          ...business,
          phone: this.userSession.phone || '',
        };
      }

      updateCartRequest.documentId = business.documentId;
      updateCartRequest.ecommerceDocumentId = this.userSession.documentId;
      if (invoiceAddress) {
        updateCartRequest.invoiceAddress = invoiceAddress;
      } else if (invoiceAddressId) {
        updateCartRequest.invoiceAddressId = invoiceAddressId;
      }
    }

    if (!isLoggedIn && guest) {
      const formmatedPhone = `${environment.phone.code}${guest.phone}`;
      const guestAddress = this.checkoutStorageService.getDeliveryAddress();

      const createGuestRequest: CreateGuestRequestDto = {
        email: guest.email ?? '',
        documentId: guest.documentId ?? '',
        firstName: guest.firstName ?? '',
        lastName: guest.lastName ?? '',
        phone: formmatedPhone,
      };
      const isDelivery =
        this.cartSession.shipment?.deliveryMode === 'delivery';
      if (isDelivery) {
        if (!guestAddress) {
          throw new Error('No se ha seleccionado una dirección de entrega');
        }
        createGuestRequest.address = {
          location: guestAddress.location,
          city: guestAddress.city,
          region: guestAddress.region,
          province: guestAddress.province,
          number: guestAddress.number,
          street: guestAddress.street,
          departmentOrHouse: guestAddress.departmentHouse,
          reference: guestAddress.reference,
          latitude: guestAddress.lat ? +guestAddress.lat : null,
          longitude: guestAddress.lng ? +guestAddress.lng : null,
        };
      }
      updateCartRequest.guest = createGuestRequest;
    }

    /*if (
      !this.isLoggedIn &&
      paymentDocumentType === PaymentDocumentType.INVOICE
    ) {
      const { business } = params;
      updateCartRequest.business = {
        documentId: business.documentId,
        businessName: '',
        businessLineName: '',
        businessLineCode: '',
        email: '',
        phone: '',
      };
    }*/
    return firstValueFrom(this.cartApiService.prepay(updateCartRequest));
  }

  /**
   * Crear invitado y guardar datos del carro de compras.
   */
  /*
  private async prepararCarroPrePago(): Promise<void> {
    let guest = this.guestStorage.get();
    if (!guest) return;
    try {
      const formmatedPhone = `${environment.phone.code}${guest.phone}`;
      const guestAddress = this.checkoutStorageService.getDeliveryAddress();

      const createGuestRequest: CreateGuestRequestDto = {
        email: guest.email ?? '',
        documentId: guest.documentId ?? '',
        firstName: guest.firstName ?? '',
        lastName: guest.lastName ?? '',
        phone: formmatedPhone,
      };
      const isDelivery =
        this.cartSession.shipment?.deliveryMode === 'delivery';
      if (isDelivery) {
        if (!guestAddress) {
          throw new Error('No se ha seleccionado una dirección de entrega');
          //return;
        }
        createGuestRequest.address = {
          location: guestAddress.location,
          city: guestAddress.city,
          region: guestAddress.region,
          province: guestAddress.province,
          number: guestAddress.number,
          street: guestAddress.street,
          departmentOrHouse: guestAddress.departmentHouse,
          reference: guestAddress.reference,
          latitude: guestAddress.lat ? +guestAddress.lat : null,
          longitude: guestAddress.lng ? +guestAddress.lng : null,
        };
      }

      const address = await firstValueFrom(
        this.customerApiService.createGuestUser(createGuestRequest)
      );
      if (isDelivery) {
        await firstValueFrom(
          this.cartService.updateAddressIdShipment({
            shoppingCartId: this.cartSession._id || '',
            addressId: address.addresses[0].id,
          })
        );
      }
      await firstValueFrom(
        this.cartService.saveTemp({
          shoppingCartId: this.cartSession._id
            ? this.cartSession._id.toString()
            : '',
          documentId: guest.documentId,
          email: guest.email,
        })
      );
    } catch (e) {
      console.error(e);
      console.log('No pudo guardarse los datos del invitado');
    }
  }*/

  /**
   * Solicitar aprobación de la orden de compra.
   * @param data
   */
  purchaseRequestAll(data: any): void {
    this.addPaymentInfoToGTM('OC');
    let file_aux = data.file[0];
    const partes = file_aux.name.split('.');
    const extension = partes[partes.length - 1];
    let aux: Archivo = {
      archivo: data.file[0],
      nombre: data.name,
      icon: 'far fa-file-pdf text-red-500 fa-lg',
      extension,
    };
    let info = {
      userRole: this.userSession?.userRole,
      shoppingCartId: this.cartSession._id!.toString(),
      file: aux.archivo,
      costCenter: data.costCenterCode,
      number: data.code,
      amount: this.totalCarro,
    };
    this.paymentMethodPurchaseOrderRequestService.upload2(info).subscribe({
      next: (r) => {
        this.purchaseOrderId = r.purchaseOrder._id.toString();
        this.purchaseRequest();
      },
      error: (e) => {
        this.blockUiService.set(false);
        console.error(e);
      },
    });
  }

  /**
   * Solicitar aprobación de la orden de compra.
   */
  purchaseRequest(): void {
    this.paymentMethodPurchaseOrderRequestService
      .requestApproval(this.cartSession._id!, DocumentType.SALES_ORDER)
      .subscribe({
        next: () => {
          this.cartService.load();
          this.router.navigate([
            '/',
            'carro-compra',
            'comprobante-de-solicitud',
          ]);
        },
        error: (e) => {
          this.blockUiService.set(false);
          console.log(e);
        },
      });
  }

  private async paymentOV(data: any): Promise<void> {
    try {
      this.addPaymentInfoToGTM('OC');
      let file_aux = data.file[0];
      const partes = file_aux.name.split('.');
      const extension = partes[partes.length - 1];
      let aux: Archivo = {
        archivo: data.file[0],
        nombre: data.name,
        icon: 'far fa-file-pdf text-red-500 fa-lg',
        extension,
      };
      let info = {
        userRole: this.userSession?.userRole,
        shoppingCartId: this.cartSession._id!.toString(),
        file: aux.archivo,
        costCenter: data.costCenterCode,
        number: data.code,
        amount: this.totalCarro,
      };
      this.paymentMethodPurchaseOrderRequestService.upload2(info).subscribe({
        next: async (r) => {
          this.purchaseOrderId = r.purchaseOrder._id.toString();
          //await this.prepararCarroPrePago();
          await this.finishPaymentOv(r.purchaseOrder);
        },
        error: (e) => {
          this.blockUiService.set(false);
          console.error(e);
        },
      });
    } catch (e) {
      console.log(e);
    }
  }

  private async finishPaymentOv(
    paymentPurchaseOrder: IPaymentPurchaseOrder | null
  ): Promise<void> {
    if (!paymentPurchaseOrder) {
      this.blockUiService.set(false);
      return;
    }
    let params = {
      status: 'approved',
      paymentMethod: 'OC',
      shoppingCartId: this.cartSession._id!.toString(),
      shoppingCartNumber: this.cartSession.cartNumber,
    };
    this.cartService.load();
    this.purchaseOrderId = paymentPurchaseOrder._id.toString();
    if (!params.shoppingCartNumber)
      params.shoppingCartNumber = paymentPurchaseOrder.shoppingCartNumber;
    this.router.navigate(['/', 'carro-compra', 'gracias-por-tu-compra'], {
      queryParams: { ...params },
    });
  }

  /**
   * Crear pago con orden de compra y sms.
   */
  private async paymentOvSms({ costCenterCode, code }: any): Promise<void> {
    try {
      this.addPaymentInfoToGTM('OC');
      let info = {
        userRole: this.userSession?.userRole,
        shoppingCartId: this.cartSession._id!.toString(),
        file: null,
        costCenter: costCenterCode,
        number: code,
        amount: this.totalCarro,
        credito: false,
      };
      if (this.userSession.creditLine) {
        if (
          this.totalCarro >= this.userSession.creditLine.fromAmount &&
          (this.totalCarro < this.userSession.creditLine.toAmount ||
            this.userSession.creditLine.toAmount == -1)
        ) {
          info.credito = true;
        } else {
          info.credito = false;
        }
        this.paymentMethodPurchaseOrderRequestService.upload2(info).subscribe({
          next: (response) => {
            this.purchaseOrderId = response.purchaseOrder._id;
            if (!info.credito) {
              this.purchaseRequest();
            } else {
              this.blockUiService.set(false);
              const ref = this.dialogService.open(SmsModalComponent, {
                header: 'Confirmación de compra',
                width: '50vw',
                modal: true,
                data: {
                  purchaseOrderId: this.purchaseOrderId,
                  phone: response.phoneOtp,
                },
                contentStyle: { overflow: 'auto' },
                breakpoints: {
                  '960px': '75vw',
                  '640px': '90vw',
                },
              });
              ref.onClose.subscribe(
                (value: {
                  isValid: boolean;
                  purchaseOrder: IPaymentPurchaseOrder;
                }) => {
                  if (value.isValid) this.finishPaymentOv(value.purchaseOrder);
                }
              );
            }
          },
          error: (e) => {
            this.blockUiService.set(false);
            console.log(e);
          },
        });
      }
    } catch (e) {
      this.blockUiService.set(false);
      console.log(e);
    }
  }

  private reloadShipping() {
    if (this.step() === 3) {
      this.dialogService.open(ReloadModalComponent, {
        header: 'Aviso',
        width: '50vw',
        modal: true,
        contentStyle: { overflow: 'auto' },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
      });
      this.step.set(2);
    }
  }

  ngOnDestroy(): void {
    this.blockUiService.set(false);
  }
}
